import { ArrowRight } from 'lucide-react-native';
import { Pressable, StyleSheet } from 'react-native';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { HeroWatermark } from '@/components/HeroWatermark';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { Overline } from '@/components/Overline';
import { Text } from '@/components/Text';
import { radii, spacing } from '@/constants/theme';

export interface AvailableBalanceCardProps {
  balanceCents: number;
  lifetimeCents: number;
  minCents: number;
  onCashOut: () => void;
  onHistory: () => void;
}

/**
 * Home's hero when cash-out is live (`user.cashout_enabled`). Mirrors the home
 * design contract: available balance is the actionable hero; lifetime earned is
 * demoted to a subline so it isn't lost. When the flag is off, HomeTab renders
 * `HeroEarningsCard` (lifetime) instead — the two are interchangeable at the
 * same slot, which is why they share `radii['2xl']` and the same dark surface.
 */
export function AvailableBalanceCard({
  balanceCents,
  lifetimeCents,
  minCents,
  onCashOut,
  onHistory,
}: AvailableBalanceCardProps) {
  const lifetimeDollars = (lifetimeCents / 100).toFixed(0);
  const balanceDollars = (balanceCents / 100).toFixed(2);
  return (
    <Card variant="dark" style={styles.balanceCard}>
      <HeroWatermark />
      <Overline tone="onDark">Available to cash out</Overline>
      <MoneyDisplay cents={balanceCents} size="hero" tone="onDark" showCents />
      <Text variant="caption" color="textOnDarkMuted" style={styles.lifetimeSubline}>
        ${lifetimeDollars} earned all-time
      </Text>
      <Text variant="bodyMedium" color="textOnDarkMuted" style={styles.balanceCaption}>
        Venmo, PayPal, Visa &amp; gift cards
      </Text>
      <Button
        variant="primary"
        size="base"
        fullWidth
        label="Cash out"
        trailingIcon={ArrowRight}
        disabled={balanceCents < minCents}
        onPress={onCashOut}
        accessibilityLabel={`Cash out, $${balanceDollars} available`}
        testID="home-cashout-cta"
      />
      <Pressable
        onPress={onHistory}
        accessibilityRole="button"
        accessibilityLabel="View cash out history"
        style={styles.historyLink}
        testID="home-cashout-history"
      >
        <Text variant="caption" color="textOnDarkMuted">
          Cash out history →
        </Text>
      </Pressable>
    </Card>
  );
}

const styles = StyleSheet.create({
  balanceCard: {
    padding: spacing.lg,
    borderRadius: radii['2xl'],
    overflow: 'hidden',
  },
  lifetimeSubline: {
    marginTop: spacing.xs,
  },
  balanceCaption: {
    marginTop: spacing.xs,
    marginBottom: spacing.base,
  },
  historyLink: {
    marginTop: spacing.md,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
